import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UI_THEME } from '../ui/theme';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const primaryButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';

  const shortcuts = [
    { label: 'Emendas', description: 'Consultar fila, totais e detalhes dos processos.', to: '/dashboard/emendas' },
    { label: 'Instituições', description: 'Acompanhar cadastros e situação documental.', to: '/dashboard/instituicoes' },
    { label: 'Parlamentares', description: 'Gerenciar parlamentares e dados de contato.', to: '/dashboard/parlamentares' },
  ];

  const userFields = [
    { label: 'Nome', value: user?.name || 'N/A' },
    { label: 'E-mail', value: user?.email || 'N/A' },
    { label: 'Perfil', value: user?.role || 'N/A' },
  ];

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Workspace' : 'Painel'}
            </p>
            <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
              Acesso rápido
            </h1>
            <p className={`mt-2 max-w-3xl text-sm leading-6 ${bodyTextClass}`}>
              Use este painel para entrar nas principais áreas de gestão de emendas, instituições e cadastros administrativos.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition ${secondaryButtonClass}`}
          >
            Voltar para Home
          </button>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          <div className={`rounded-3xl border p-5 ${surfaceClass}`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className={`text-lg font-semibold ${strongTextClass}`}>Emendas</h2>
                <p className={`mt-1 text-sm ${bodyTextClass}`}>
                  Abra a visão operacional para filtrar, acompanhar etapas e consultar detalhes de cada processo.
                </p>
              </div>
              <Link
                to="/dashboard/emendas"
                className={`inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold transition ${primaryButtonClass}`}
              >
                Acessar Emendas
              </Link>
            </div>
          </div>

          <div className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
            <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <h3 className={`text-base font-semibold ${strongTextClass}`}>Cadastros</h3>
              <p className={`mt-1 text-sm ${bodyTextClass}`}>
                Atalhos para rotinas frequentes do painel.
              </p>
            </div>
            <div className="grid divide-y divide-slate-200 dark:divide-slate-800 md:grid-cols-3 md:divide-x md:divide-y-0">
              {shortcuts.map((shortcut) => (
                <Link
                  key={shortcut.to}
                  to={shortcut.to}
                  className="group block min-h-[128px] px-5 py-4 transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
                >
                  <div className={`text-sm font-semibold group-hover:underline ${strongTextClass}`}>{shortcut.label}</div>
                  <p className={`mt-2 text-sm leading-5 ${bodyTextClass}`}>{shortcut.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <aside className={`rounded-3xl border p-5 ${surfaceClass}`}>
          <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>Usuário</p>
          <div className="mt-4 space-y-4">
            {userFields.map((field) => (
              <div key={field.label}>
                <p className={`text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>{field.label}</p>
                <p className={`mt-1 break-words text-sm font-medium ${strongTextClass}`}>{field.value}</p>
              </div>
            ))}
          </div>
        </aside>
      </section>
    </div>
  );
};

export default DashboardPage;
